// One boolean admin config switch, read and written through the admin config
// API and shared by every master-switch panel.
//
// Fail-closed, matching the server rule exactly: a missing row (404), an
// unreadable row, or any shape other than an explicit `true` all mean OFF. A
// screen that said "on" while the server behaved as "off" would be the worst
// kind of false confidence, so there is no optimistic update either — the
// truth always comes back from the server.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiUrl } from '../lib/api'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** True only when the row says exactly `true`; anything else is off. */
export function parseEnabled(payload: unknown): boolean {
  if (!isRecord(payload) || !isRecord(payload.config_value)) return false
  return payload.config_value.value === true
}

export type SwitchReadResult = { enabled: boolean; readFailed: boolean }

async function readBooleanConfig(configKey: string): Promise<SwitchReadResult> {
  const res = await fetch(apiUrl(`/admin/config/${configKey}`))
  // 404 = the row was never written. That is the normal starting state, not a
  // fault: a switch is designed to be absent until somebody turns it on.
  if (res.status === 404) return { enabled: false, readFailed: false }
  if (!res.ok) return { enabled: false, readFailed: true }
  try {
    return { enabled: parseEnabled(await res.json()), readFailed: false }
  } catch {
    return { enabled: false, readFailed: true }
  }
}

async function writeBooleanConfig(configKey: string, next: boolean): Promise<boolean> {
  const res = await fetch(apiUrl(`/admin/config/${configKey}`), {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ config_value: { value: next }, value_type: 'boolean' }),
  })
  if (!res.ok) throw new Error(`${configKey} write failed: ${res.status}`)
  return next
}

function adminConfigQueryKey(configKey: string) {
  return ['admin', 'config', configKey] as const
}

export function useAdminBooleanConfig(configKey: string) {
  const queryClient = useQueryClient()
  const queryKey = adminConfigQueryKey(configKey)
  const { data, isError } = useQuery({ queryKey, queryFn: () => readBooleanConfig(configKey) })
  const mutation = useMutation({
    mutationFn: (next: boolean) => writeBooleanConfig(configKey, next),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })
  return {
    enabled: data?.enabled ?? false,
    readFailed: isError || (data?.readFailed ?? false),
    setEnabled: mutation.mutate,
    isSaving: mutation.isPending,
    writeFailed: mutation.isError,
  }
}
